import React, { useEffect, useMemo, useRef, useState } from 'react';
import AddHospitalModal from './AddHospitalModal.jsx';
import { fetchHospitalList, addHospital } from '../../helper/HospitalHelper.js';
import SignUpModal from './signUpModal.jsx';
import { useNavigate } from 'react-router-dom';
import PageStickyChrome from '../../components/PageStickyChrome/PageStickyChrome';
import PageFilterSearch from '../../components/PageStickyChrome/PageFilterSearch';
import { PageStatIcon } from '../../components/PageStickyChrome/PageStatIcons';
import './HospitalDashboard.css';
import FindDeviceModal from './FindDevice.jsx';

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const HospitalCard = ({ hospital, onClick }) => (
  <article
    className="hospital-card"
    onClick={() => onClick(hospital)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(hospital);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="hospital-card-head">
      <h3 className="hospital-card-name">{displayValue(hospital.name)}</h3>
      <span className="hospital-card-pin">{displayValue(hospital.pincode)}</span>
    </div>

    <div className="hospital-card-details">
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">ADDRESS</span>
        <span className="hospital-card-detail-val hospital-card-detail-val--wrap">
          {displayValue(hospital.address)}
        </span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">CITY</span>
        <span className="hospital-card-detail-val">{displayValue(hospital.city)}</span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">STATE</span>
        <span className="hospital-card-detail-val">{displayValue(hospital.state)}</span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">COUNTRY</span>
        <span className="hospital-card-detail-val">{displayValue(hospital.country)}</span>
      </div>
    </div>

    <div className="hospital-card-foot">
      <span>View wards</span>
      <span aria-hidden="true">›</span>
    </div>
  </article>
);

const HospitalDashboard = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const [hospitals, setHospitals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry] = useState('India');
  const [statesInCountry, setStatesInCountry] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [countriesWithStates, setCountriesWithStates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [hospitalName, setHospitalName] = useState('');
  const [showFindDeviceModal, setShowFindDeviceModal] = useState(false);

  const loadHospitals = () => {
    fetchHospitalList()
      .then((res) => setHospitals(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    loadHospitals();
  }, []);

  useEffect(() => {
    if (showSuccessToast) {
      loadHospitals();
    }
  }, [showSuccessToast]);

  useEffect(() => {
    fetch('https://countriesnow.space/api/v0.1/countries/states')
      .then((res) => res.json())
      .then((data) => setCountriesWithStates(data.data || []))
      .catch((err) => console.error('Error fetching countries and states:', err));
  }, []);

  useEffect(() => {
    if (countriesWithStates.length > 0) {
      const india = countriesWithStates.find((country) => country.name === 'India');
      if (india) {
        setStatesInCountry(india.states || []);
      }
    }
  }, [countriesWithStates]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleAddHospital = (newHospital) => {
    if (!newHospital.hospitalName) return;

    addHospital(newHospital)
      .then(() => {
        setHospitalName(newHospital.hospitalName);
        setIsModalOpen(false);
        setShowSuccessToast(true);
        setSelectedState('');
        setSearchQuery('');

        setTimeout(() => {
          setShowSuccessToast(false);
          setShowSignUp(true);
        }, 3000);
      })
      .catch((err) => {
        console.error('Error adding hospital:', err);
        alert(err);
      });
  };

  const handleSignUpSubmit = () => {};

  const filteredHospitals = useMemo(() => hospitals.filter((hospital) => {
    const matchesSearch = hospital?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCountry = !selectedCountry || hospital?.country === selectedCountry;
    const matchesState = !selectedState || hospital?.state === selectedState;
    return matchesSearch && matchesCountry && matchesState;
  }), [hospitals, searchQuery, selectedCountry, selectedState]);

  const handleRowClick = (record) => {
    navigate(`/hospital-details/wards?hospitalId=${record.id}`);
  };

  return (
    <div className="hospital-page">
      {showSuccessToast && (
        <div className="hospital-toast" role="status" aria-live="polite">
          <span className="hospital-toast-icon" aria-hidden="true">✓</span>
          <div className="hospital-toast-text">
            <strong>Hospital added successfully</strong>
            <span>The hospital has been added to the dashboard.</span>
          </div>
        </div>
      )}

      <PageStickyChrome>
      <div className="hospital-header">
        <h3 className="hospital-title">Hospital Dashboard</h3>
      </div>

      <div className="hospital-filters-bar">
        <PageFilterSearch inputRef={searchInputRef}>
          <input
            ref={searchInputRef}
            type="text"
            className="hospital-search-input"
            placeholder="Search hospital name..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </PageFilterSearch>
        <select
          className="hospital-filter-select"
          name="hospital-dashboard-state-filter"
          autoComplete="off"
          value={selectedState}
          onChange={(event) => setSelectedState(event.target.value)}
          aria-label="Filter by state"
        >
          <option value="">All States</option>
          {statesInCountry.map((state, index) => (
            <option key={`${state.name}-${index}`} value={state.name}>
              {state.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="hospital-btn hospital-btn--ghost"
          onClick={() => setShowFindDeviceModal(true)}
        >
          <span className="hospital-btn-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M16 16l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </span>
          <span className="hospital-btn-label">Find Machine</span>
        </button>
        <button
          type="button"
          className="hospital-btn hospital-btn--primary"
          onClick={() => setIsModalOpen(true)}
        >
          <span className="hospital-btn-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M12 6v12M6 12h12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </span>
          <span className="hospital-btn-label">Add Hospital</span>
        </button>
      </div>

      <div className="hospital-stats-grid">
        <div className="hospital-stat-card hospital-stat-card--total">
          <PageStatIcon name="hospital" />
          <span className="hospital-stat-label">TOTAL HOSPITALS</span>
          <span className="hospital-stat-value">{filteredHospitals.length}</span>
        </div>
      </div>

      <div className="hospital-toolbar">
        <h2 className="hospital-section-title">Hospitals</h2>
      </div>
      </PageStickyChrome>

      <div className="hospital-cards-scroll">
        {filteredHospitals.length === 0 ? (
          <div className="hospital-empty">No hospitals found.</div>
        ) : (
          <div className="hospital-cards-grid">
            {filteredHospitals.map((hospital) => (
              <HospitalCard
                key={hospital.id}
                hospital={hospital}
                onClick={handleRowClick}
              />
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddHospitalModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleAddHospital}
          countriesWithStates={countriesWithStates}
        />
      )}

      {showSignUp && (
        <SignUpModal
          onClose={() => setShowSignUp(false)}
          onSignUpSubmit={handleSignUpSubmit}
          hospitalName={hospitalName}
        />
      )}

      {showFindDeviceModal && (
        <FindDeviceModal onClose={() => setShowFindDeviceModal(false)} />
      )}
    </div>
  );
};

export default HospitalDashboard;
